/**
 * Full-screen predictive search overlay, replacing the header's plain GET
 * search form. Focusing either header search input (desktop or mobile
 * drawer) opens the overlay and mirrors the typed value into its own field;
 * typing there fetches Shopify's native `/search/suggest.json` (debounced)
 * and renders bolded-match results, with resting (recent searches, device-
 * only via localStorage), loading, empty, and error states.
 *
 * DOM contract: `[data-predictive-search]` is the overlay root, containing
 * `[data-predictive-search-field]` (its own input), `[data-predictive-state]`
 * elements (resting/loading/results/empty/error), `[data-predictive-results]`
 * (results <ul>), `[data-predictive-recent-list]` (recent-searches <ul>),
 * `[data-predictive-view-all]` (link to the full results page), and
 * `[data-predictive-search-close]` (close triggers: scrim + button).
 */
const RECENTS_KEY = 'vellum:recentSearches';
const RECENTS_MAX = 8;
const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;

function readRecents() {
  try {
    return JSON.parse(window.localStorage.getItem(RECENTS_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

function addRecent(term) {
  const trimmed = term.trim();
  if (!trimmed) return;
  const existing = readRecents().filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase());
  existing.unshift(trimmed);
  window.localStorage.setItem(RECENTS_KEY, JSON.stringify(existing.slice(0, RECENTS_MAX)));
}

function highlightMatch(text, query) {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;
  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);
  const wrapper = document.createElement('span');
  wrapper.append(document.createTextNode(before));
  const mark = document.createElement('mark');
  mark.textContent = match;
  wrapper.append(mark, document.createTextNode(after));
  return wrapper;
}

class VellumPredictiveSearch {
  constructor(root) {
    this.root = root;
    this.field = root.querySelector('[data-predictive-search-field]');
    this.results = root.querySelector('[data-predictive-results]');
    this.recentList = root.querySelector('[data-predictive-recent-list]');
    this.recentSection = root.querySelector('[data-predictive-recent]');
    this.viewAllLink = root.querySelector('[data-predictive-view-all]');
    this.emptyText = root.querySelector('[data-predictive-empty-text]');
    this.debounceTimer = null;
    this.lastRequestId = 0;

    document.querySelectorAll('[data-predictive-search-input]').forEach((input) => {
      input.addEventListener('focus', () => this.open(input.value));
    });

    root.querySelectorAll('[data-predictive-search-close]').forEach((closer) => {
      closer.addEventListener('click', () => this.close());
    });

    this.field?.addEventListener('input', () => this.onInput());
    this.field?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submit(this.field.value);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !root.hidden) this.close();
    });

    this.renderRecents();
  }

  open(initialValue) {
    this.root.hidden = false;
    if (this.field) {
      this.field.value = initialValue || '';
      this.field.focus();
    }
    this.renderRecents();
    if (initialValue && initialValue.trim().length >= MIN_CHARS) {
      this.search(initialValue.trim());
    } else {
      this.setState('resting');
    }
  }

  close() {
    this.root.hidden = true;
  }

  setState(state) {
    this.root.querySelectorAll('[data-predictive-state]').forEach((el) => {
      el.hidden = el.dataset.predictiveState !== state;
    });
    if (this.viewAllLink) this.viewAllLink.hidden = state !== 'results';
  }

  renderRecents() {
    const recents = readRecents();
    if (this.recentSection) this.recentSection.hidden = recents.length === 0;
    if (!this.recentList) return;

    this.recentList.innerHTML = '';
    recents.forEach((term) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = '#';
      link.textContent = term;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.field.value = term;
        this.search(term);
      });
      li.append(link);
      this.recentList.append(li);
    });
  }

  onInput() {
    const query = this.field.value.trim();
    window.clearTimeout(this.debounceTimer);

    if (query.length < MIN_CHARS) {
      this.setState('resting');
      return;
    }

    this.debounceTimer = window.setTimeout(() => this.search(query), DEBOUNCE_MS);
  }

  async search(query) {
    this.setState('loading');
    if (this.viewAllLink) {
      const url = new URL(this.viewAllLink.href, window.location.origin);
      url.searchParams.set('q', query);
      this.viewAllLink.href = url.toString();
    }

    const requestId = ++this.lastRequestId;

    try {
      const rootPath = window.Shopify?.routes?.root || '/';
      const suggestUrl = new URL(`${rootPath}search/suggest.json`.replace(/\/{2,}/g, '/'), window.location.origin);
      suggestUrl.searchParams.set('q', query);
      suggestUrl.searchParams.set('resources[type]', 'product');
      suggestUrl.searchParams.set('resources[limit]', '6');
      suggestUrl.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(suggestUrl.toString());
      if (requestId !== this.lastRequestId) return;
      if (!response.ok) throw new Error(`${response.status}`);

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (products.length === 0) {
        if (this.emptyText) this.emptyText.textContent = `No results for "${query}"`;
        this.setState('empty');
        return;
      }

      this.renderResults(products, query);
      this.setState('results');
    } catch (error) {
      if (requestId !== this.lastRequestId) return;
      this.setState('error');
    }
  }

  renderResults(products, query) {
    if (!this.results) return;
    this.results.innerHTML = '';

    products.forEach((product) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'predictive-search__result';
      link.href = product.url;

      const media = document.createElement('span');
      media.className = 'predictive-search__result-media';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        media.append(img);
      }

      const info = document.createElement('span');
      const title = document.createElement('span');
      title.className = 'predictive-search__result-title';
      title.append(highlightMatch(product.title, query));

      const price = document.createElement('span');
      price.className = 'predictive-search__result-price';
      price.textContent = product.price || '';

      info.append(title, price);
      link.append(media, info);
      li.append(link);
      this.results.append(li);
    });
  }

  submit(query) {
    const trimmed = query.trim();
    if (!trimmed) return;
    addRecent(trimmed);

    const rootPath = window.Shopify?.routes?.root || '/';
    const searchUrl = new URL(`${rootPath}search`.replace(/\/{2,}/g, '/'), window.location.origin);
    searchUrl.searchParams.set('q', trimmed);
    searchUrl.searchParams.set('type', 'product,article,page');
    window.location.href = searchUrl.toString();
  }
}

document.querySelectorAll('[data-predictive-search]').forEach((root) => new VellumPredictiveSearch(root));
